import React from "react";
import groupUngroup from "../../assets/icons/icon-center-horizontal.svg";
import alignleft from "../../assets/icons/icon-alignleft.svg";
import aligncenterYaxis from "../../assets/icons/icon-aligncenter.svg";
import alignright from "../../assets/icons/icon-alignright.svg";
import aligncenterXaxis from "../../assets/icons/icon-horizontal.svg";
import alignbottom from "../../assets/icons/icon-alignbottom.svg";
import aligntop from "../../assets/icons/icon-aligntop.svg";
import duplicate from "../../assets/icons/icon-group.svg";
import idelete from "../../assets/icons/icon-idelete.svg";
import { showPopup } from "../../Store/reducers/popUpSlice";
import { useDispatch } from "react-redux";

const AlignmentControls = ({ fabricCanvas, saveCanvasState }) => {
  const offSet = 20;
  const dispatch = useDispatch();

  const getViewportData = () => {
    
    const zoom = fabricCanvas.getZoom();
    const [a, b, c, d, panX, panY] = fabricCanvas.viewportTransform;
    const visibleWidth = fabricCanvas.getWidth() / zoom;
    const visibleHeight = fabricCanvas.getHeight() / zoom;
    return { zoom, panX, panY, visibleWidth, visibleHeight };
  };

  const containsProtectedObject = (obj) => {
    if (obj.protected) return true;
  
    if (obj._objects?.length) {
      return obj._objects.some(containsProtectedObject);
    }
  
    return false;
  };

  const deleteActiveObject = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;

    if (
      (activeObject.type === "group" || activeObject.type === "activeSelection" ) &&
      containsProtectedObject(activeObject)
    ) {
      dispatch(showPopup("This Group Contains a Protected Svg"));
      return false;
    }

    if (activeObject.protected) {
      dispatch(showPopup("This Svg Cannot be Deleted"));
      return false;
    }

    if (activeObject.type === "activeSelection") {
      activeObject.getObjects().forEach((obj) => {
        fabricCanvas.remove(obj);
      });
    } else {
      fabricCanvas.remove(activeObject);
    }

    fabricCanvas.discardActiveObject();
    fabricCanvas.renderAll();

    saveCanvasState();
  };

  const handleDuplicate = () => {
    const activeObj = fabricCanvas.getActiveObject();
    if (!activeObj) {
      console.error("No active object selected.");
      return;
    }

    const offset = 20;

    if (activeObj.type === "activeSelection") {
      const groupMatrix = activeObj.calcTransformMatrix();

      activeObj.getObjects().forEach((obj) => {
        obj.clone((cloneObj) => {
          const localPoint = new fabric.Point(obj.left, obj.top);
          const absolutePoint = fabric.util.transformPoint(
            localPoint,
            groupMatrix
          );

          const newLeft = absolutePoint.x + offset;
          const newTop = absolutePoint.y + offset;

          cloneObj.set({
            left: newLeft,
            top: newTop,
          });
          cloneObj.setCoords();
          fabricCanvas.add(cloneObj);
        });
      });

      fabricCanvas.discardActiveObject();
      fabricCanvas.renderAll();
      saveCanvasState();
    } else {
      activeObj.clone((cloneObj) => {
        cloneObj.set({
          left: activeObj.left + offset,
          top: activeObj.top + offset,
        });
        cloneObj.setCoords();
        fabricCanvas.add(cloneObj);
        fabricCanvas.renderAll();
        saveCanvasState();
      });
    }
  };

  const alignLeft = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panX, zoom } = getViewportData();
    const leftVisible = -panX / zoom;
    let newLeft = leftVisible;

    if (activeObject.originX === "center") {
      newLeft += activeObject.getScaledWidth() / 2 + offSet / zoom;
    }
    activeObject.set({ left: newLeft }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignRight = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panX, zoom, visibleWidth } = getViewportData();
    const rightVisible = -panX / zoom + visibleWidth;
    let newLeft;
    if (activeObject.originX === "center") {
      newLeft =
        rightVisible - activeObject.getScaledWidth() / 2 - offSet / zoom;
    } else {
      newLeft = rightVisible - activeObject.getScaledWidth();
    }
    activeObject.set({ left: newLeft }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignCenterX = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panX, zoom, visibleWidth } = getViewportData();
    const centerVisible = -panX / zoom + visibleWidth / 2;
    let newLeft;
    if (activeObject.originX === "center") {
      newLeft = centerVisible;
    } else {
      newLeft = centerVisible - activeObject.getScaledWidth() / 2;
    }
    activeObject.set({ left: newLeft }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignTop = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panY, zoom } = getViewportData();
    const topVisible = -panY / zoom;
    let newTop = topVisible;
    if (activeObject.originY === "center") {
      newTop += activeObject.getScaledHeight() / 2 + offSet / zoom;
    }
    activeObject.set({ top: newTop }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignBottom = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panY, zoom, visibleHeight } = getViewportData();
    const bottomVisible = -panY / zoom + visibleHeight;
    let newTop;
    if (activeObject.originY === "center") {
      newTop =
        bottomVisible - activeObject.getScaledHeight() / 2 - offSet / zoom;
    } else {
      newTop = bottomVisible - activeObject.getScaledHeight();
    }
    activeObject.set({ top: newTop }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const alignCenterY = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) return;
    const { panY, zoom, visibleHeight } = getViewportData();
    const centerVisible = -panY / zoom + visibleHeight / 2;
    let newTop;
    if (activeObject.originY === "center") {
      newTop = centerVisible;
    } else {
      newTop = centerVisible - activeObject.getScaledHeight() / 2;
    }
    activeObject.set({ top: newTop }).setCoords();
    fabricCanvas.renderAll();
    saveCanvasState();
  };

  const toggleGroup = (event) => {
    const activeObj = fabricCanvas.getActiveObject();
    if (!activeObj) return;

    fabricCanvas._isGrouping = true;

    if (activeObj.type === "group") {
      const pointer = fabricCanvas.getPointer(event);
      let clickedObject;

      activeObj._objects.forEach((obj) => {
        if (obj.containsPoint(pointer)) {
          clickedObject = obj;
        }
      });

      // Convert group to active selection (ungrouping)
      activeObj.toActiveSelection();

      // Reassign canvas and update coordinates for each object after ungrouping.
      activeObj.getObjects().forEach((obj) => {
        obj.canvas = fabricCanvas;
        obj.setCoords();
      });

    
      if (clickedObject) {
        fabricCanvas.setActiveObject(clickedObject);
      }
    } else if (activeObj.type === "activeSelection") {
      
      activeObj.toGroup();

     
      activeObj.getObjects().forEach((obj) => {
        obj.canvas = fabricCanvas;
        obj.setCoords();
      });
      activeObj.setCoords();
    }

    fabricCanvas.renderAll();
    saveCanvasState();

    setTimeout(() => {
      fabricCanvas._isGrouping = false;
    }, 100);
  };

  return (
    <div className="flex items-center justify-center space-x-3 cursor-pointer">
      <img
        src={groupUngroup}
        alt="Group/Ungroup Icon"
        className="w-13px h-13px"
        onClick={toggleGroup}
      />
      <img
        src={alignleft}
        alt="Align Left Icon"
        className="w-13px h-13px"
        onClick={alignLeft}
      />
      <img
        src={aligncenterYaxis}
        alt="Align Center Y Icon"
        className="w-13px h-13px"
        onClick={alignCenterY}
      />
      <img
        src={alignright}
        alt="Align Right Icon"
        className="w-13px h-13px"
        onClick={alignRight}
      />
      <img
        src={aligncenterXaxis}
        alt="Align Center X Icon"
        className="w-13px h-13px"
        onClick={alignCenterX}
      />
      <img
        src={alignbottom}
        alt="Align Bottom Icon"
        className="w-13px h-13px"
        onClick={alignBottom}
      />
      <img
        src={aligntop}
        alt="Align Top Icon"
        className="w-13px h-13px"
        onClick={alignTop}
      />
      <img
        src={duplicate}
        alt="Duplicate Icon"
        className="w-13px h-13px"
        onClick={handleDuplicate}
      />
      <img
        src={idelete}
        alt="Delete Icon"
        className="w-13px h-13px"
        onClick={deleteActiveObject}
      />
    </div>
  );
};

export default AlignmentControls;
